import { Fragment } from "react";
import { Box, Typography, useTheme } from "@mui/material";

export default function HoverIconButton(props) {
  const {
    cypressId,
    disabled,
    downloadDocType,
    exportItemType,
    handleClick,
    icon,
    iconDirection,
    text,
    variant = "primary", // primary, secondary, success, danger
  } = props;

  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, mode } = palette;
  const isDark = mode === "dark";

  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";

  // Variant color mapping with dark mode support
  const variantColors = {
    primary: {
      bg: isDark ? `${greenColor}12` : `${navyColor}08`,
      bgHover: isDark ? `${greenColor}20` : `${navyColor}15`,
      border: isDark ? `${greenColor}30` : `${navyColor}20`,
      borderHover: isDark ? `${greenColor}50` : `${navyColor}40`,
      text: isDark ? greenColor : navyColor,
      icon: isDark ? greenColor : navyColor,
    },
    secondary: {
      bg: isDark ? "rgba(148, 163, 184, 0.08)" : "#f8fafc",
      bgHover: isDark ? "rgba(148, 163, 184, 0.15)" : "#f1f5f9",
      border: isDark ? "rgba(148, 163, 184, 0.2)" : "#e2e8f0",
      borderHover: isDark ? "rgba(148, 163, 184, 0.35)" : "#cbd5e1",
      text: isDark ? "#94a3b8" : "#475569",
      icon: isDark ? "#94a3b8" : "#64748b",
    },
    success: {
      bg: `${greenColor}${isDark ? '15' : '10'}`,
      bgHover: `${greenColor}${isDark ? '25' : '20'}`,
      border: `${greenColor}${isDark ? '40' : '30'}`,
      borderHover: `${greenColor}${isDark ? '60' : '50'}`,
      text: greenColor,
      icon: greenColor,
    },
    danger: {
      bg: isDark ? "rgba(248, 113, 113, 0.12)" : "rgba(239, 68, 68, 0.08)",
      bgHover: isDark ? "rgba(248, 113, 113, 0.2)" : "rgba(239, 68, 68, 0.15)",
      border: isDark ? "rgba(248, 113, 113, 0.35)" : "rgba(239, 68, 68, 0.25)",
      borderHover: isDark ? "rgba(248, 113, 113, 0.5)" : "rgba(239, 68, 68, 0.4)",
      text: isDark ? "#f87171" : "#ef4444",
      icon: isDark ? "#f87171" : "#ef4444",
    },
  };

  const colors = variantColors[variant] || variantColors.primary;

  function handleKeyPress(e) {
    if (e.key === "Enter" || e.code === "Space") {
      e.target.click();
    }
  }

  const disabledBg = isDark ? "rgba(148, 163, 184, 0.08)" : "#f8fafc";
  const disabledBorder = isDark ? "rgba(148, 163, 184, 0.15)" : "#e2e8f0";
  const disabledText = isDark ? "#64748b" : "#94a3b8";

  const iconElement = icon && (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: disabled ? disabledText : colors.icon,
        transition: "all 0.2s ease-in-out",
        "& svg": {
          fontSize: "1.125rem",
        },
      }}
    >
      {icon}
    </Box>
  );

  return (
    <Box
      component="button"
      data-cypress-id={cypressId}
      onKeyPress={handleKeyPress}
      onClick={() => {
        if (!disabled) handleClick();
      }}
      role="button"
      tabIndex={disabled ? -1 : 0}
      disabled={disabled}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 16px",
        borderRadius: "8px",
        border: `1px solid ${disabled ? disabledBorder : colors.border}`,
        backgroundColor: disabled ? disabledBg : colors.bg,
        color: disabled ? disabledText : colors.text,
        cursor: disabled ? "not-allowed" : "pointer",
        boxShadow: disabled ? "none" : (isDark ? "0 1px 3px rgba(0,0,0,0.2)" : "0 1px 2px rgba(0,0,0,0.05)"),
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        fontFamily: '"Lato", sans-serif',
        fontSize: "0.875rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        outline: "none",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: `linear-gradient(135deg, transparent 0%, ${disabled ? "transparent" : colors.bgHover} 100%)`,
          opacity: 0,
          transition: "opacity 0.25s ease-in-out",
        },
        "&:hover:not(:disabled)": {
          backgroundColor: colors.bgHover,
          borderColor: colors.borderHover,
          transform: "translateY(-1px)",
          boxShadow: `0 4px 14px ${disabled ? "transparent" : `${colors.border}40`}, 0 1px 3px rgba(0,0,0,0.08)`,
          "&::before": {
            opacity: 0.5,
          },
        },
        "&:active:not(:disabled)": {
          transform: "translateY(0)",
          boxShadow: `0 1px 3px ${disabled ? "transparent" : colors.border}`,
        },
        "&:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 3px ${colors.border}`,
        },
      }}
    >
      {iconDirection === "left" ? (
        <>
          {iconElement}
          <Typography
            component="span"
            sx={{
              fontSize: "inherit",
              fontWeight: "inherit",
              lineHeight: 1,
              position: "relative",
              zIndex: 1,
            }}
          >
            {text || ""}
          </Typography>
          {downloadDocType && (
            <span className="screen-reader-only">
              {exportItemType ? `selected ${exportItemType}` : null}{" "}
              {downloadDocType} file download
            </span>
          )}
        </>
      ) : (
        <>
          <Typography
            component="span"
            sx={{
              fontSize: "inherit",
              fontWeight: "inherit",
              lineHeight: 1,
              position: "relative",
              zIndex: 1,
            }}
          >
            {text || ""}
          </Typography>
          {downloadDocType && (
            <span className="screen-reader-only">
              {exportItemType ? `selected ${exportItemType}` : null}{" "}
              {downloadDocType} file download
            </span>
          )}
          {iconElement}
        </>
      )}
    </Box>
  );
}